import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, MoreVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { OrganizationDialog } from './components/OrganizationDialog';
import { OrganizationStatus } from '../../core/models/Organization';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/DropdownMenu';
import { useAuth } from '../../core/contexts/AuthContext';
import { UserRole } from '../../core/models/User';

export const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizations, loading, error, createOrganization, updateOrganization } = useOrganizations();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<typeof organizations[0] | null>(null);
  const { currentUser } = useAuth();

  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;

  const handleCreateOrganization = async (data: any) => {
    try {
      await createOrganization({
        ...data,
        clientIds: [],
      });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create client:', err);
      const errorMessage = err?.message || 'Failed to create client. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateOrganization = async (data: any) => {
    if (!editingOrganization) return;
    try {
      await updateOrganization(editingOrganization.id, data);
      setEditingOrganization(null);
    } catch (err: any) {
      console.error('Failed to update client:', err);
      const errorMessage = err?.message || 'Failed to update client. Please try again.';
      alert(errorMessage);
    }
  };

  const getStatusColor = (status: OrganizationStatus) => {
    switch (status) {
      case OrganizationStatus.ACTIVE:
        return 'success';
      case OrganizationStatus.INACTIVE:
        return 'error';
      case OrganizationStatus.SUSPENDED:
        return 'warning';
      case OrganizationStatus.PENDING:
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <LoadingState message="Loading clients..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load clients" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clients"
        description="Manage clients and their settings"
        icon={Building2}
        actions={
          isSuperAdmin ? (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Button>
          ) : null
        }
      />

      {organizations.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No clients found"
          description="Get started by adding your first client"
          action={{
            label: 'Add Client',
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <Card
              key={org.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/organizations/${org.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{org.name}</CardTitle>
                    <Badge variant={getStatusColor(org.status)} className="mt-2">
                      {org.status}
                    </Badge>
                  </div>
                  {isSuperAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingOrganization(org);
                          }}
                        >
                          Edit
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  <p>{org.clientIds?.length || 0} Projects</p>
                </div>
                {org.address && (
                  <div className="text-xs text-muted-foreground mt-2">
                    {org.address.city}, {org.address.state}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Client Dialog */}
      <OrganizationDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateOrganization}
      />

      {/* Edit Client Dialog */}
      {editingOrganization && (
        <OrganizationDialog
          open={!!editingOrganization}
          onClose={() => setEditingOrganization(null)}
          organization={editingOrganization}
          onSubmit={handleUpdateOrganization}
        />
      )}
    </div>
  );
};
